import { UpaTarget } from '../../services/upa.service';
import { Component, ElementRef, Injector, Input, ViewChild, ViewEncapsulation, Inject, PLATFORM_ID, OnDestroy, AfterViewInit } from '@angular/core';
import { AbstractComponent } from '../abstract.component';
import { Module } from 'projects/common/model/ModuleModel';
import { EventDispatcherService } from 'projects/common/services/event-dispatcher.service';
import { isPlatformBrowser } from '@angular/common';
import { ModuleService } from 'projects/common/services/module.service';

@Component({
  selector: 'event-dispatcher',
  template: '',
  encapsulation: ViewEncapsulation.None
})
export class EventDispatcherComponent extends AbstractComponent implements OnDestroy, AfterViewInit {
  private static MODULE_ID: string = 'EventDispatcherModule';

  @Input() override module: Module<EventDispatcherData>;
  @Input() uniqueId?: string;
  @Input() moduleIndex: number;

  @ViewChild('eventDispatcherTrigger') eventDispatcherTrigger: ElementRef;

  constructor(injector: Injector,
    private eventDispatcherService: EventDispatcherService,
    @Inject(PLATFORM_ID) private platformId: string,
    private hostElement: ElementRef,
    private moduleService: ModuleService
  ) {

    super(injector);
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.module.id === "SofiaPromptSuggestionsModule") {

      this.waitFloatModuleAndDispatch();
    } else {

      this.syncAndDispatch();
    }
  }

  waitFloatModuleAndDispatch() {
    if (window.floatModuleHasBeenLoaded) {

      this.syncAndDispatch();
    } else {
      
      document.addEventListener("floatModuleHasBeenLoaded", this.handleFloatModuleLoaded.bind(this));
    }
  }
  
  handleFloatModuleLoaded(event: Event) {
    document.removeEventListener("floatModuleHasBeenLoaded", this.handleFloatModuleLoaded.bind(this));
    this.syncAndDispatch();
  }

  handleEventToListenAck(event: Event) {
    const customEvent = event as CustomEvent;
    if (this.module.id === "SofiaPromptSuggestionsModule" && this.module.data.event_to_listen_ack === "firstOpenApp.complete") {
      if (customEvent.detail && !customEvent.detail.value){
        // si NO es firstOpenApp, no se muestra el título flotante
        delete this.module.data.event_payload.floating_title;
      }
      this.dispatchEvent();
      return;
    }
    if (customEvent.detail && customEvent.detail.value) {
      this.dispatchEvent();
    }
  }

  syncAndDispatch() {
    if (this.module.data.event_to_dispatch_syn) {

      document.dispatchEvent(new Event(this.module.data.event_to_dispatch_syn));
    }

    if (this.module.data.event_to_listen_ack) {

      document.addEventListener(this.module.data.event_to_listen_ack, this.handleEventToListenAck.bind(this));
    } else {

      const isInitialEvent = !this.module.data.dispatch_on_previous_module_scroll && !this.module.data.associated_module_unique_id;
      if (isInitialEvent) {

        this.dispatchEvent();
      } else {

        this.dispatchOnScroll();
      }
    }
  }

  dispatchOnScroll() {
    let targetModule;
        
    if (this.module.data.associated_module_unique_id) {
      targetModule = document.getElementById(this.module.data.associated_module_unique_id);
    } else {
      targetModule = this.hostElement.nativeElement.closest('.nymeria-module').previousElementSibling;
    }
    
    if (targetModule) {
      this.moduleService.onElementVisible(targetModule, () => {
        this.dispatchEvent();
      }, false, { rootMargin: '-50% 0px -50% 0px' });
    }
  }

  buildEventToDispatch() {
    return new CustomEvent(this.module.data.event_to_dispatch, { detail: this.module.data.event_payload });
  }

  dispatchEvent() {
    if (this.module.data.event_to_dispatch) {
      const event = this.buildEventToDispatch();

      const isDispatched = this.eventDispatcherService.dispatchEvent(event, this.uniqueId);

      if (isDispatched) {
        const upaData = this.upaService.resolveData({
          label: `${event.type} - ${event.detail.floating_title?.title}`,
          moduleId: EventDispatcherComponent.MODULE_ID,
          eventtype: 'dispatch-event',
          target: UpaTarget.EVENT_DISPATCHER,
          uniqueId: this.uniqueId,
          moduleIndex: this.moduleIndex
        });
        this.upaService.trackTopic('landing', upaData);
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener("floatModuleHasBeenLoaded", this.handleFloatModuleLoaded.bind(this));
    }

    if (isPlatformBrowser(this.platformId) && this.module?.data?.event_to_listen_ack) {
      document.removeEventListener(this.module.data.event_to_listen_ack, this.handleEventToListenAck.bind(this));
    }
  }

  getModuleId() {
    return EventDispatcherComponent.MODULE_ID;
  }
}

export interface EventDispatcherData {
  event_to_dispatch: string;
  event_payload?: any;
  event_to_dispatch_syn?: string;
  event_to_listen_ack?: string;
  dispatch_on_previous_module_scroll?: boolean;
  associated_module_unique_id?: string;
}

// Extend the Window interface to include floatModuleHasBeenLoaded
declare global {
  interface Window {
    floatModuleHasBeenLoaded?: boolean;
  }
}